import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import { usePermissions } from '@/hooks/usePermissions';
import { useCanUpdateAccount } from '@/hooks/useCanUpdateAccount';
import { Card, Text, H2 } from '@payaca/ambrosia';
import { AppliancesPermissions } from '@payaca/permissions/appliances/appliances.permissions';
import { EstimatesPermissions } from '@payaca/permissions/estimates/estimates.permissions';
import { FC } from 'react';
import AppliancesSection from './AppliancesSection';
import DefaultEstimateItemsSection from './DefaultEstimateItemsSection';
import CompanySettingsSolarTariffs from './CompanySettingsSolarTariffs';
import CompanySettingsSolarPlacement from './CompanySettingsSolarPlacement';
import CompanySettingsSolarBattery from './CompanySettingsSolarBattery';

const ProductOff: FC<{ brand: 'Flow' | 'Glow'; descriptor: string }> = ({
  brand,
  descriptor,
}) => (
  <Card sizeVariant="lg">
    <Card.Body className="flex flex-col gap-4">
      <div>
        <H2>{brand} isn&apos;t on for this account</H2>
        <Text>
          {brand} is the {descriptor} design tool. It is switched on per
          account. Talk to the Payaca team to turn it on.
        </Text>
      </div>
    </Card.Body>
  </Card>
);

export const CompanySettingsFlow: FC = () => {
  const hasHeatDesignTool = useMyAccountHasFeature('hasHeatDesignTool');
  const { userHasRequiredPermission } = usePermissions();
  const canViewAppliances = userHasRequiredPermission([
    AppliancesPermissions.MANAGE_APPLIANCES,
  ]);
  const canViewDefaultItems = userHasRequiredPermission([
    EstimatesPermissions.GET_DEFAULT_ITEMS,
  ]);

  if (!hasHeatDesignTool) {
    return <ProductOff brand="Flow" descriptor="heating and cooling" />;
  }

  return (
    <>
      {canViewAppliances && <AppliancesSection product="flow" />}
      {canViewDefaultItems && (
        <DefaultEstimateItemsSection category="HEAT_PUMP" />
      )}
    </>
  );
};

export const CompanySettingsGlow: FC = () => {
  const hasSolarDesignTool = useMyAccountHasFeature('hasSolarDesignTool');
  const canUpdateAccount = useCanUpdateAccount();
  const { userHasRequiredPermission } = usePermissions();
  const canViewAppliances = userHasRequiredPermission([
    AppliancesPermissions.MANAGE_APPLIANCES,
  ]);
  const canViewDefaultItems = userHasRequiredPermission([
    EstimatesPermissions.GET_DEFAULT_ITEMS,
  ]);

  if (!hasSolarDesignTool) {
    return <ProductOff brand="Glow" descriptor="solar" />;
  }

  return (
    <>
      {canViewAppliances && <AppliancesSection product="glow" />}
      {canViewDefaultItems && <DefaultEstimateItemsSection category="SOLAR" />}
      <CompanySettingsSolarTariffs readOnly={!canUpdateAccount} />
      <CompanySettingsSolarPlacement readOnly={!canUpdateAccount} />
      <CompanySettingsSolarBattery readOnly={!canUpdateAccount} />
    </>
  );
};
